import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isSubmittableResponse } from "../../utils";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";

export interface UseSubmitFormArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  formId: string;
  enduserId: string;
  responseId?: string;
  parentResponseId?: string;
  rootResponseId?: string;
}

export const useSubmitForm = ({
  core,
  formId,
  enduserId,
  responseId,
  parentResponseId,
  rootResponseId,
}: UseSubmitFormArgs) => {
  const { t } = useTranslation("forms");

  return useCallback(async () => {
    const isValid = await core.validateAllFields();
    if (!isValid) {
      throw new Error(t("Please fix validation errors before submitting"));
    }

    return core.runSubmit(async (uploadFirst) => {
      let response = core.responseQuery.data ?? core.preparedResponse;

      if (!responseId && !response) {
        const res = await core.prepareFormResponseMutation.mutateAsync({
          formId,
          enduserId,
          parentResponseId,
          rootResponseId,
          title: core.formTitle,
        });
        response = res.response;
      }

      if (!response || !response.accessCode) {
        throw new Error(t("Failed to prepare form response"));
      }

      const responsesAfterUpload = await uploadFirst();

      const result = await core.submitFormMutation.mutateAsync({
        accessCode: response.accessCode,
        responses: Object.values(responsesAfterUpload).filter(isSubmittableResponse),
        markedAsSubmitted: true,
      });

      return result;
    });
  }, [core, enduserId, formId, parentResponseId, responseId, rootResponseId, t]);
};
